import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import useAppDispatch from "../../hooks/useAppDispatch";
import "./App.scss";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { login, logout } from "../../store/userProfile/userProfileSlice";
import { AuthService } from "../../services/auth.service";
import { getAccessToken } from "../../helper/auth";
import { setAllCountries } from "../../store/countries/countriesSlice";
import Message from "../Message/Message";
import useAppSelector from "../../hooks/useAppSelector";
import { UserSubscriptionService } from "../../services/userSubscription.service";
import {
  setUserSubscription,
  setUserSubscriptionToInitial,
} from "../../store/userSubscription/userSubscriptionSlice";
import { setPaymentMethodsToInitial } from "../../store/userPaymentMethods/userPaymentMethodsSlice";
import { setLocationToInitial } from "../../store/userLocation/userLocationSlice";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import {
  useGetAllCountries,
  useGetClassEventReservationByIdQuery,
  useGetServiceAvailabilityQuery,
} from "../../api/queries";
import { useGetReviewAvailabilityQuery } from "../../api/queries/reviewAvailability";
import MaintenanceModal from "../MaintenanceModal/MaintenanceModal";
import LeaveARatingModal from "../LeaveARatingModal/LeaveARatingModal";

const App: React.FC = () => {
  const navigate = useNavigate();

  const { data: serviceAvailability } = useGetServiceAvailabilityQuery();

  const onCloseMaintenanceModal = () => {
    window.location.reload();
  };

  const lastNavigatedEventIdRef = useRef<string | null>(null);

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const dispatch = useAppDispatch();
  const [classForReview, setClassForReview] = useState<any>(null);

  const { data: countries } = useGetAllCountries();

  const { data: reviewAvailability = null, isSuccess: isReviewAvailSuccess } =
    useGetReviewAvailabilityQuery(user);

  const classEventQueryArgs = useMemo(
    () => ({
      classEventId: reviewAvailability?.classEventId ?? null,
      gymListingId: reviewAvailability?.gymListingId ?? null,
    }),
    [reviewAvailability?.classEventId, reviewAvailability?.gymListingId]
  );

  const { data: classEvent, isSuccess: isClassEventSuccess } =
    useGetClassEventReservationByIdQuery(classEventQueryArgs);

  useEffect(() => {
    if (!isClassEventSuccess || !classEvent) return;

    const currentId = String(classEvent.id ?? classEvent.classEventId ?? "");
    if (!currentId) return;

    if (lastNavigatedEventIdRef.current !== currentId) {
      lastNavigatedEventIdRef.current = currentId;
      setClassForReview(classEvent);
    }
  }, [classEvent, isClassEventSuccess, navigate]);

  useEffect(() => {
    if (getAccessToken()) {
      AuthService.getCurrentUser().then((res) => {
        if (res?.data && res.status === 200) {
          dispatch(login(res.data));
        } else {
          dispatch(logout());
          AuthService.logout();
          dispatch(setPaymentMethodsToInitial());
          dispatch(setLocationToInitial());
          dispatch(setUserSubscriptionToInitial());
        }
      });
    } else {
      dispatch(logout());
      AuthService.logout();
      dispatch(setPaymentMethodsToInitial());
      dispatch(setLocationToInitial());
      dispatch(setUserSubscriptionToInitial());
    }
  }, [getAccessToken()]);

  useEffect(() => {
    if (!countries) return;
    dispatch(setAllCountries(countries));
  }, [countries]);

  useEffect(() => {
    if (user) {
      fetchUserSubscription(user.id);
    }
  }, [user]);

  const fetchUserSubscription = async (userId: string) => {
    try {
      const response = await UserSubscriptionService.getUserSubscriptionByUserId(userId);
      if (response.data && response.status === 200) {
        dispatch(setUserSubscription(response.data));
      }
    } catch (e) {}
  };

  return (
    <>
      <ScrollToTop />
      <div className="layout">
        <Message />
        <Header />
        <div className="central-element">
          <Outlet />
        </div>
        <Footer />
      </div>

      <MaintenanceModal open={serviceAvailability === 1} onClose={onCloseMaintenanceModal} />

      <LeaveARatingModal
        open={!!classForReview}
        onClose={() => setClassForReview(null)}
        onSuccessLeaveARating={() => setClassForReview(null)}
        event={classForReview}
      />
    </>
  );
};

export default App;
